import React, { useEffect, useState } from 'react';
import { Leaf } from 'lucide-react';
import { tenantBrand } from '../../utils/tenantBranding';

function BrandImage({ src, alt, className, style }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <img src={src} alt={alt} className={className} style={style} onError={() => setFailed(true)} />
  );
}

/** Média de luma dos pixels opacos — decide se a marca precisa de placa escura. */
function useOpaqueLuma(src) {
  const [luma, setLuma] = useState(null);

  useEffect(() => {
    if (!src) {
      setLuma(null);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const w = Math.max(1, Math.min(img.naturalWidth || 1, 160));
        const h = Math.max(1, Math.min(img.naturalHeight || 1, 80));
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let sum = 0;
        let n = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 28) continue;
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          n += 1;
        }
        if (!cancelled) setLuma(n ? sum / n : null);
      } catch {
        if (!cancelled) setLuma(null);
      }
    };
    img.onerror = () => {
      if (!cancelled) setLuma(null);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return luma;
}

export function TenantCoatMark({
  tenant,
  size = 40,
  className = '',
  fallback = true,
  alt,
  plate = false,
  square = true,
}) {
  const { coatOfArmsUrl, logoUrl } = tenantBrand(tenant);
  const src = coatOfArmsUrl || logoUrl;
  const dim =
    typeof size === 'number'
      ? square
        ? { height: size, width: size }
        : { width: size, maxWidth: '100%', height: 'auto' }
      : {};
  if (src) {
    const image = (
      <BrandImage
        src={src}
        alt={alt || tenant?.name || 'Brasão'}
        className={`shrink-0 object-contain object-center ${className}`}
        style={dim}
      />
    );
    if (plate === 'white' || plate === true) {
      const box = typeof size === 'number' ? size + 16 : undefined;
      return (
        <span
          className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-white p-2 shadow-sm ring-1 ring-black/10"
          style={box ? { width: box, height: box } : undefined}
        >
          {image}
        </span>
      );
    }
    return image;
  }
  if (!fallback) return null;
  return <Leaf className={className || 'logo-icon'} style={dim} aria-hidden />;
}

export function TenantLogo({ tenant, className = '', alt, plate = false }) {
  const { logoUrl } = tenantBrand(tenant);
  const luma = useOpaqueLuma(plate === 'auto' ? logoUrl : null);
  if (!logoUrl) return null;
  const image = (
    <BrandImage
      src={logoUrl}
      alt={alt || tenant?.name || 'Logo'}
      className={`object-contain ${className}`}
    />
  );
  if (plate === 'white') {
    return (
      <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-black/10">
        {image}
      </span>
    );
  }
  const needsDarkPlate = plate === true || (plate === 'auto' && luma != null && luma >= 145);
  if (!needsDarkPlate) return image;
  return (
    <span className="inline-flex items-center rounded-lg bg-slate-950 px-2.5 py-1.5">
      {image}
    </span>
  );
}

export function TenantBanner({ tenant, className = '', alt }) {
  const { bannerUrl } = tenantBrand(tenant);
  if (!bannerUrl) return null;
  return (
    <BrandImage
      src={bannerUrl}
      alt={alt || tenant?.name || 'Banner'}
      className={`object-contain ${className}`}
    />
  );
}

export function TenantBannerBar({ tenant, className = '' }) {
  const { bannerUrl } = tenantBrand(tenant);
  if (!bannerUrl) return null;
  return (
    <div className={`shrink-0 bg-white ${className}`}>
      <BrandImage
        src={bannerUrl}
        alt={tenant?.name || 'Banner institucional'}
        className="mx-auto h-14 w-full max-w-[90rem] object-contain object-center sm:h-16"
      />
    </div>
  );
}

export function PoweredByUseAmbiental({ className = '', light = false }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
        light ? 'text-primary-200/90' : 'text-primary-300'
      } ${className}`}
    >
      <Leaf className="h-3 w-3" strokeWidth={2} aria-hidden />
      useAmbiental
    </span>
  );
}
